import type { RelationFile } from '@risksmart-app/shared/forms/shared-schemas/fileSchema';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import { ownerContributorAndViewerIds } from 'src/components/form';

import { useInsertChildAction } from '@/hooks/mutations/action/useInsertChildAction';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';

import type { ActionFormFieldData } from './update/forms/actionsSchema';

interface UseSaveActionArgs {
  /**
   * Undefined on the register page, where a new action has no parent — the
   * procedure takes `ParentId` nullable-optional and coalesces it to null.
   */
  parentId?: string;
  /** The caller's register query refetch. */
  refetch: () => void;
  /**
   * Anything the caller must do with the new action once its files are attached
   * and before the refetch renders the result. The obligation-change detail page
   * links the action to its obligation and closes its modal here.
   */
  onCreated?: (actionId: string) => void | Promise<void>;
}

/**
 * Creates an action and attaches its files, shared by the actions register, the
 * actions tab and the obligation-change detail page.
 *
 * The order matters. `useInsertChildAction` invalidates on insert success —
 * before `updateFiles` resolves — and the register reads are `no-cache`, so
 * neither that invalidation nor an Apollo eviction can refresh them. The
 * caller's `refetch` after the files land is what puts the new action on the
 * table with its files (COI-577).
 */
export const useSaveAction = ({
  parentId,
  refetch,
  onCreated,
}: UseSaveActionArgs) => {
  const { insertChildAction, loading } = useInsertChildAction();
  const { updateFiles } = useFileUpdate();

  const saveAction = async (values: ActionFormFieldData) => {
    const { files } = values;
    const result = await insertChildAction({
      ...values,
      ParentId: parentId,
      CustomAttributeData: values.CustomAttributeData || undefined,
      DepartmentTypeIds:
        values.departments?.map((d) => d.DepartmentTypeId) || [],
      TagTypeIds: values.tags?.map((t) => t.TagTypeId) || [],
      ...ownerContributorAndViewerIds(values),
    });
    const actionId = result?.insertChildAction?.Id;
    if (!actionId) {
      throw new Error('Missing actionId');
    }
    await updateFiles({
      parentType: Parent_Type_Enum.Action,
      parentId: actionId,
      originalFiles: values.files.filter(
        (f) => !(f instanceof File)
      ) as RelationFile[],
      selectedFiles: files,
    });
    await onCreated?.(actionId);
    refetch();
  };

  return { saveAction, loading };
};
